import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function useAppVersionCheck(): void {
  const notifiedRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (!import.meta.env.PROD || notifiedRef.current) {
      return;
    }

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const data = await response.json() as { version?: string };
      if (!data.version || data.version === __APP_VERSION__) {
        return;
      }

      notifiedRef.current = true;

      toast(
        (t) => (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Nova versão disponível</span>
            <span className="text-xs text-gray-300">
              Atualize para ver as últimas melhorias do sistema.
            </span>
            <button
              type="button"
              className="mt-1 rounded bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
              onClick={() => {
                toast.dismiss(t.id);
                window.location.reload();
              }}
            >
              Atualizar agora
            </button>
          </div>
        ),
        {
          id: 'app-version-update',
          duration: Infinity,
        }
      );
    } catch {
      // Ignora falhas de rede em verificações em background
    }
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) {
      return;
    }

    void checkVersion();

    const handleFocus = () => {
      void checkVersion();
    };

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, CHECK_INTERVAL_MS);

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);
}
